import { useState, useEffect } from 'react';
import { X, Shield, Check, ChevronDown, ChevronRight, LayoutDashboard, Table, Users, Truck, UserCheck, Building, Clock, Settings, FileText } from 'lucide-react';
import api from '../api/config';
import './PermissionsModal.css';

const MAESTRO_ETAPAS = [
  {
    id: 'etapa1',
    nombre: 'Etapa 1 - Cliente',
    columnas: [
      { key: 'item', label: 'Item' },
      { key: 'cliente', label: 'Cliente' },
      { key: 'cotizacion', label: 'Cotización' },
      { key: 'p_order', label: 'Orden de Compra' },
      { key: 'item2', label: 'Item 2' },
      { key: 'descripcion', label: 'Descripción' },
      { key: 'part_number', label: 'Nro. Parte' },
      { key: 'cin', label: 'C.I.N.' },
      { key: 'delivery_date', label: 'Fecha Entrega' },
      { key: 'real_delivery', label: 'Entrega Real' },
      { key: 'cantidad', label: 'Cantidad' },
      { key: 'unidad', label: 'Unidad' },
      { key: 'pu_clp', label: 'P. Unitario CLP' },
      { key: 'total_neto', label: 'Total Neto' }
    ]
  },
  {
    id: 'etapa2',
    nombre: 'Etapa 2 - Facturación',
    columnas: [
      { key: 'guia', label: 'Guía' },
      { key: 'factura', label: 'Factura' },
      { key: 'emision_fact', label: 'Emisión' },
      { key: 'vencimiento', label: 'Vencimiento' },
      { key: 'fecha_pago', label: 'Fecha Pago' },
      { key: 'pronto_pago', label: 'Pronto Pago' },
      { key: 'utilidad_factor', label: 'Utilidad Factor' }
    ]
  },
  {
    id: 'etapa3',
    nombre: 'Etapa 3 - Proveedor',
    columnas: [
      { key: 'proveedor', label: 'Proveedor' },
      { key: 'oc_sobek', label: 'OC Proveedor' },
      { key: 'pu_proveedor', label: 'P.U. Proveedor' },
      { key: 'total_proveedor', label: 'Total Proveedor' },
      { key: 'u_del_negocio', label: 'Utilidad Negocio' },
      { key: 'factura_proveedor', label: 'Factura Proveedor' },
      { key: 'emision_prov', label: 'Emisión Prov.' },
      { key: 'vencimiento_prov', label: 'Vencimiento Prov.' },
      { key: 'fecha_pago_prov', label: 'Fecha Pago Prov.' }
    ]
  },
  {
    id: 'etapa4',
    nombre: 'Etapa 4 - Observaciones',
    columnas: [
      { key: 'observaciones', label: 'Observaciones' }
    ]
  }
];

const getDefaultMaestroConfig = () => {
  const etapas = {};
  MAESTRO_ETAPAS.forEach(etapa => {
    const columns = {};
    etapa.columnas.forEach(col => { columns[col.key] = true; });
    etapas[etapa.id] = { enabled: true, columns };
  });
  return { etapas };
};

const PermissionsModal = ({ isOpen, onClose, member, onSave, currentUserRole, isSuperAdmin }) => {
  const [permissions, setPermissions] = useState({
    sections: {
      dashboard: true,
      maestro: false,
      clientes: false,
      proveedores: false,
      equipo: false,
      empresa: false,
      documentos: false,
      historial: false
    },
    maestroConfig: getDefaultMaestroConfig()
  });
  const [expandedMaestro, setExpandedMaestro] = useState(false);
  const [expandedEtapas, setExpandedEtapas] = useState({});
  const [originalPermissions, setOriginalPermissions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showMessage, setShowMessage] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Verificar si el usuario puede editar permisos (solo admin, editor o superadmin)
  const canEditPermissions = isSuperAdmin || currentUserRole === 'admin' || currentUserRole === 'editor';

  const sectionConfig = {
    dashboard: { label: 'Dashboard', desc: 'Panel principal con estadísticas', icon: LayoutDashboard },
    maestro: { label: 'Maestro', desc: 'Tabla de órdenes y operaciones', icon: Table },
    clientes: { label: 'Clientes', desc: 'Gestión de clientes', icon: Users },
    proveedores: { label: 'Proveedores', desc: 'Gestión de proveedores', icon: Truck },
    equipo: { label: 'Equipo', desc: 'Gestión del equipo de trabajo', icon: UserCheck },
    empresa: { label: 'Empresa', desc: 'Configuración de la empresa', icon: Building },
    documentos: { label: 'Documentos', desc: 'Gestión de documentos adjuntos', icon: FileText },
    historial: { label: 'Historial', desc: 'Historial de actividades', icon: Clock }
  };

  useEffect(() => {
    if (member && member.permissions) {
      const perms = {
        ...member.permissions,
        maestroConfig: member.permissions.maestroConfig || getDefaultMaestroConfig()
      };
      setPermissions(perms);
      setOriginalPermissions(JSON.parse(JSON.stringify(perms)));
      setHasChanges(false);
      setExpandedMaestro(false);
      setExpandedEtapas({});
    }
  }, [member]);

  useEffect(() => {
    if (originalPermissions) {
      const changed = JSON.stringify(permissions) !== JSON.stringify(originalPermissions);
      setHasChanges(changed);
    }
  }, [permissions, originalPermissions]);

  const handleToggleSection = (section) => {
    if (!canEditPermissions) return;

    setPermissions(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [section]: !prev.sections[section]
      }
    }));
    if (section === 'maestro') {
      setExpandedMaestro(false);
      setExpandedEtapas({});
    }
  };

  const handleToggleEtapa = (etapaId) => {
    if (!canEditPermissions) return;
    setPermissions(prev => ({
      ...prev,
      maestroConfig: {
        ...prev.maestroConfig,
        etapas: {
          ...prev.maestroConfig.etapas,
          [etapaId]: {
            ...prev.maestroConfig.etapas[etapaId],
            enabled: !prev.maestroConfig.etapas[etapaId].enabled
          }
        }
      }
    }));
  };

  const handleToggleColumn = (etapaId, colKey) => {
    if (!canEditPermissions) return;
    setPermissions(prev => ({
      ...prev,
      maestroConfig: {
        ...prev.maestroConfig,
        etapas: {
          ...prev.maestroConfig.etapas,
          [etapaId]: {
            ...prev.maestroConfig.etapas[etapaId],
            columns: {
              ...prev.maestroConfig.etapas[etapaId].columns,
              [colKey]: !prev.maestroConfig.etapas[etapaId].columns[colKey]
            }
          }
        }
      }
    }));
  };

  const handleToggleAllColumns = (etapaId, value) => {
    if (!canEditPermissions) return;
    setPermissions(prev => {
      const cols = { ...prev.maestroConfig.etapas[etapaId].columns };
      Object.keys(cols).forEach(k => { cols[k] = value; });
      return {
        ...prev,
        maestroConfig: {
          ...prev.maestroConfig,
          etapas: {
            ...prev.maestroConfig.etapas,
            [etapaId]: { ...prev.maestroConfig.etapas[etapaId], columns: cols }
          }
        }
      };
    });
  };

  const handleSave = async () => {
    if (!hasChanges) return;

    setLoading(true);
    try {
      await onSave(member.id, permissions);
      setMessage({ type: 'success', text: 'Permisos actualizados exitosamente' });
      setShowMessage(true);
      setTimeout(() => {
        onClose();
        setShowMessage(false);
        setMessage({ type: '', text: '' });
      }, 2000);
    } catch (error) {
      console.error('Error saving permissions:', error);
      setMessage({ type: 'error', text: 'Error al guardar permisos' });
      setShowMessage(true);
      setTimeout(() => {
        setShowMessage(false);
        setMessage({ type: '', text: '' });
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !member) return null;

  return (
    <div className="permissions-modal-overlay">
      <div className="permissions-modal">
        <div className="permissions-modal-header">
          <div className="header-info">
            <Shield size={24} />
            <div>
              <h2>Permisos de {member.nombre}</h2>
              <p>Configura el acceso a las diferentes secciones del sistema</p>
            </div>
          </div>
          <button className="permissions-modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {showMessage ? (
          <div className={`permissions-message ${message.type}`}>
            {message.type === 'success' ? (
              <div className="message-content">
                <Check size={64} />
                <h3>{message.text}</h3>
              </div>
            ) : (
              <div className="message-content">
                <X size={64} />
                <h3>{message.text}</h3>
              </div>
            )}
          </div>
        ) : (
        <div className="permissions-content">
          {!canEditPermissions && (
            <p className="perm-no-access-warning">
              No tienes permisos para modificar los permisos de otros usuarios
            </p>
          )}
          <div className="perm-sections-list">
            {Object.entries(sectionConfig).map(([key, config]) => {
              const IconComp = config.icon;
              const isActive = permissions.sections[key];
              const themeColor = localStorage.getItem('theme-primary-color') || '#c4fffc';
              return (
                <div key={key} className={`perm-section-card ${isActive ? 'active' : ''}`}
                  style={!canEditPermissions ? { opacity: 0.6, pointerEvents: 'none' } : {}}>
                  <div className="perm-section-row" onClick={() => handleToggleSection(key)}>
                    <div className="perm-section-icon" style={isActive ? { background: themeColor, borderColor: '#000' } : {}}>
                      <IconComp size={18} />
                    </div>
                    <div className="perm-section-info">
                      <span className="perm-section-label">{config.label}</span>
                      <span className="perm-section-desc">{config.desc}</span>
                    </div>
                    <div className={`perm-switch ${isActive ? 'on' : ''}`} style={isActive ? { backgroundColor: themeColor, borderColor: '#000' } : {}}>
                      <div className="perm-switch-thumb" />
                    </div>
                  </div>

                  {key === 'maestro' && isActive && (
                    <div className="perm-maestro-area">
                      <button
                        type="button"
                        className="perm-maestro-trigger"
                        onClick={() => setExpandedMaestro(!expandedMaestro)}
                      >
                        <Settings size={14} />
                        <span>Configurar etapas y columnas visibles</span>
                        <ChevronDown size={14} className={`perm-chevron ${expandedMaestro ? 'open' : ''}`} />
                      </button>

                      {expandedMaestro && (
                        <div className="perm-maestro-body">
                          {MAESTRO_ETAPAS.map(etapa => {
                            const etapaConfig = permissions.maestroConfig?.etapas?.[etapa.id] || { enabled: true, columns: {} };
                            const isEtapaExpanded = expandedEtapas[etapa.id];
                            const enabledCount = etapa.columnas.filter(c => etapaConfig.columns?.[c.key] !== false).length;
                            const etapaColors = {
                              etapa1: '#10b981', etapa2: '#f43f5e',
                              etapa3: '#f59e0b', etapa4: '#8b5cf6'
                            };

                            return (
                              <div key={etapa.id} className={`perm-etapa ${etapaConfig.enabled !== false ? '' : 'disabled'}`}>
                                <div className="perm-etapa-row">
                                  <div className="perm-etapa-left">
                                    <div className="perm-etapa-dot" style={{ background: etapaColors[etapa.id] }} />
                                    <span className="perm-etapa-title">{etapa.nombre}</span>
                                    {etapaConfig.enabled !== false && (
                                      <span className="perm-etapa-count">{enabledCount}/{etapa.columnas.length}</span>
                                    )}
                                  </div>
                                  <div className="perm-etapa-right">
                                    {etapaConfig.enabled !== false && (
                                      <button
                                        type="button"
                                        className="perm-etapa-cols-btn"
                                        onClick={() => setExpandedEtapas(prev => ({ ...prev, [etapa.id]: !prev[etapa.id] }))}
                                      >
                                        <ChevronDown size={14} className={`perm-chevron ${isEtapaExpanded ? 'open' : ''}`} />
                                      </button>
                                    )}
                                    <div
                                      className={`perm-switch sm ${etapaConfig.enabled !== false ? 'on' : ''}`}
                                      style={etapaConfig.enabled !== false ? { backgroundColor: etapaColors[etapa.id] } : {}}
                                      onClick={() => handleToggleEtapa(etapa.id)}
                                    >
                                      <div className="perm-switch-thumb" />
                                    </div>
                                  </div>
                                </div>

                                {etapaConfig.enabled !== false && isEtapaExpanded && (
                                  <div className="perm-cols-panel">
                                    <div className="perm-cols-header">
                                      <span>Columnas visibles</span>
                                      <div className="perm-cols-quick">
                                        <button type="button" onClick={() => handleToggleAllColumns(etapa.id, true)}>Todas</button>
                                        <button type="button" onClick={() => handleToggleAllColumns(etapa.id, false)}>Ninguna</button>
                                      </div>
                                    </div>
                                    <div className="perm-cols-grid">
                                      {etapa.columnas.map(col => {
                                        const colActive = etapaConfig.columns?.[col.key] !== false;
                                        return (
                                          <label key={col.key} className={`perm-col-chip ${colActive ? 'on' : ''}`}
                                            style={colActive ? { borderColor: etapaColors[etapa.id], background: `${etapaColors[etapa.id]}12` } : {}}>
                                            <input
                                              type="checkbox"
                                              checked={colActive}
                                              onChange={() => handleToggleColumn(etapa.id, col.key)}
                                            />
                                            <span className="perm-col-dot" style={colActive ? { background: etapaColors[etapa.id] } : {}} />
                                            <span>{col.label}</span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        )}

        {!showMessage && (
        <div className="permissions-modal-footer">
          <button
            className="btn-cancel-permissions"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            className={`btn-save-permissions ${!hasChanges || !canEditPermissions ? 'disabled' : ''}`}
            onClick={handleSave}
            disabled={loading || !hasChanges || !canEditPermissions}
            title={!canEditPermissions ? 'No tienes permisos para modificar esto' : (!hasChanges ? 'No hay cambios para guardar' : '')}
          >
            {loading ? 'Guardando...' : 'Guardar Permisos'}
          </button>
        </div>
        )}
      </div>
    </div>
  );
};

export default PermissionsModal;